import { useContext, useState } from 'react'
import { CameraAlt, CheckCircleOutlined, ErrorOutlined, InfoOutlined, NotificationsOffOutlined, WarningOutlined } from '@mui/icons-material'
import { Box, Grid, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import DevicePhotoModal from '../../../components/device-photo-modal'
import useLoadDevicePhoto from '../../../utils/hooks/useLoadDevicePhoto'
import useSharedStyles from '../../../utils/shared.styles'
import DeviceContext from '../context'
import useStyles from '../index.styles'

const getLatestImportantAlert = (device: IDeviceAPIObject) => {
  const lastCheckIn = device.lastCheckIn && new Date(device.lastCheckIn).valueOf() > 0 ? device.lastCheckIn : null

  if (!lastCheckIn) return undefined

  const activeDeviceAlerts = device.alerts
    .filter((a) => a.status === 'Active')
    .sort((a, b) => new Date(b.updatedAt).valueOf() - new Date(a.updatedAt).valueOf())

  return (
    activeDeviceAlerts.find((alert) => alert.tags.includes('urgent')) ??
    activeDeviceAlerts.find((alert) => alert.tags.includes('warning')) ??
    activeDeviceAlerts.find((alert) => alert.tags.includes('info'))
  )
}

const DevicePhoto = ({ device, setDevice }: { device: IDeviceAPIObject; setDevice: React.Dispatch<React.SetStateAction<IDeviceAPIObject | undefined>> }) => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()

  const { setDevicePhotoRefreshCounter } = useContext(DeviceContext)
  const { hasOfflineAlert, hasPhoto } = device || {}
  const { photoPath } = useLoadDevicePhoto(device?.id, !!hasPhoto, 'photo')

  const [showDevicePhotoModal, setShowDevicePhotoModal] = useState<boolean>(false)

  if (!device) return null // Ensure device is defined before proceeding

  const latestAlert = getLatestImportantAlert(device)

  let alertSeverity = 'unknown'
  if (latestAlert?.tags.includes('info')) alertSeverity = 'info'
  if (latestAlert?.tags.includes('warning')) alertSeverity = 'warning'
  if (latestAlert?.tags.includes('urgent')) alertSeverity = 'urgent'

  let response = ''
  if (latestAlert?.tags.includes('acknowledged')) response = 'acknowledged'
  if (latestAlert?.tags.includes('declined')) response = 'declined'
  if (latestAlert?.tags.includes('ignored')) response = 'ignored'

  if (device.paused) alertSeverity = 'paused'

  const type = response ? 'responded' : alertSeverity

  const { borderColor, backgroundColor } = {
    info: { borderColor: theme.palette.info.main, backgroundColor: theme.palette.primary.shades['4p'] },
    warning: { borderColor: theme.palette.warning.light, backgroundColor: theme.palette.warning.lightShades['4p'] },
    urgent: { borderColor: theme.palette.error.main, backgroundColor: theme.palette.error.shades['4p'] },
    paused: { borderColor: theme.palette.text.primary, backgroundColor: hasPhoto ? 'transparent' : 'action.hover' },
  }[alertSeverity] ?? { borderColor: theme.palette.divider, backgroundColor: hasPhoto ? 'transparent' : 'action.hover' }

  const { IconComponent, iconColor, ariaLabel } = {
    responded: { IconComponent: CheckCircleOutlined, iconColor: theme.palette.info.main, ariaLabel: response },
    info: { IconComponent: InfoOutlined, iconColor: theme.palette.info.main, ariaLabel: 'Info alert' },
    warning: { IconComponent: WarningOutlined, iconColor: theme.palette.warning.light, ariaLabel: 'Warning alert' },
    urgent: { IconComponent: ErrorOutlined, iconColor: theme.palette.error.main, ariaLabel: 'Urgent alert' },
    paused: { IconComponent: NotificationsOffOutlined, iconColor: theme.palette.text.primary, ariaLabel: 'Device is paused' },
  }[type] ?? { IconComponent: CheckCircleOutlined, iconColor: theme.palette.info.main, ariaLabel: 'Unknown' }

  const isOffline = hasOfflineAlert
  const offlineText = isOffline ? ' (Offline)' : ''

  const onSavePhoto = () => {
    setDevice((prev) => (prev ? { ...prev, hasPhoto: true } : prev)) // Update device state to reflect photo change

    // Increment counter to trigger photo refresh on device screen
    setDevicePhotoRefreshCounter((prev) => prev + 1)

    setShowDevicePhotoModal(false)
    enqueueSnackbar('Device photo updated successfully.', { variant: 'success' })
  }

  return (
    <>
      <Grid
        container={true}
        onClick={() => setShowDevicePhotoModal(true)}
        sx={[sharedClasses.centerContent, classes.imageContainer(theme), { border: `1px solid ${borderColor}`, backgroundColor }]}
      >
        {hasPhoto && photoPath ? (
          <img src={photoPath} alt="Photo of device" style={classes.imageAvatar} />
        ) : (
          <CameraAlt fontSize="large" color="secondary" data-testid="image-not-found" />
        )}
        <Box sx={classes.imageAlertIcon}>
          <IconComponent
            fontSize={alertSeverity === 'paused' ? 'medium' : 'large'}
            htmlColor={isOffline ? theme.palette.divider : iconColor}
            aria-label={`${ariaLabel}${offlineText}`}
          />
        </Box>
      </Grid>
      <DevicePhotoModal
        deviceId={device.id}
        open={showDevicePhotoModal}
        onClose={() => setShowDevicePhotoModal(false)}
        photoPath={photoPath}
        onSave={onSavePhoto}
        viewFullPhoto={hasPhoto && !!photoPath}
      />
    </>
  )
}

export default DevicePhoto
